'use client';

/* eslint-disable react-hooks/exhaustive-deps */
import Link from '@eliadmin/core/src/components/link';

import React, { useEffect, useMemo } from 'react';

import type { CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';

import { ShipmentType } from '@eliadmin/core/src/api/cart/cart-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { SignInFormCart } from '@eliadmin/core/src/components/auth/sign-in-form-cart';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { CartVatCompanyDialog } from '@eliadmin/core/src/components/cart/order/cart-vat-company-dialog';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartShipments } from '@eliadmin/core/src/hooks/cart/use-cart-shipments';
import { useCartVatContext } from '@eliadmin/core/src/hooks/cart/use-cart-vat-context';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { isNullish } from '@eliadmin/core/src/utils/misc-utils';
import { getGdprUrl, getTosUrl } from '@eliadmin/core/src/utils/gdpr-tos/get-tos-gdpr-url';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import { Heading } from '../../ui/heading';
import { Paragraph } from '../../ui/paragraph';
import { Field } from '@eliadmin/core/src/components/form/field';
import { MdOutlineEmail } from 'react-icons/md';
import { Separator } from '@eliadmin/core/src/components/ui/separator';

export function CartOrderForm() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const form = useFormContext<CreateOrderFormValues>();
  const { data: shipments } = useCartShipments();
  const { data: cart } = useCart();

  const selectedShipmentType = useMemo(() => {
    if (!isNullish(cart?.shipment?.id)) {
      return shipments?.find(shipment => String(shipment.id) === String(cart?.shipment?.id))?.type;
    }
  }, [cart?.shipment?.id, shipments]);

  const me = useCustomerStore(s => s.me);

  useEffect(() => {
    // Only clear the address when leaving a cart-owned pickup point.
    if (!form.getValues('hide_different_delivery_address')) return;
    if (!selectedShipmentType) return;
    if (selectedShipmentType !== ShipmentType.CUSTOM_PLACE) {
      form.setValue('address2', me?.customer.address_delivery ?? '');
      form.setValue('street_number2', me?.customer.street_number_delivery ?? '');
      form.setValue('postcode2', me?.customer.postcode_delivery ?? '');
      form.setValue('city2', me?.customer.city_delivery ?? '');
      form.setValue('different_delivery_address', false);
      form.setValue('hide_different_delivery_address', false);
      form.setValue('country2.id', me?.customer.country_delivery?.id ?? form.getValues('country.id') ?? '');
    }
  }, [selectedShipmentType]);

  const { data: countries } = useSuspenseQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // Profile/draft hydration belongs to the parent useCartOrderDraft hook.
    // Preserve the existing backend password workaround without replacing input.
    if (!form.getValues('password')) {
      form.setValue('password', 'BACKEND_REQUIRES_PASSWORD_REMOVE_SOMEDAY_TODO');
    }
    if (!form.getValues('password_confirmation')) {
      form.setValue('password_confirmation', 'BACKEND_REQUIRES_PASSWORD_REMOVE_SOMEDAY_TODO');
    }
  }, [me]);

  const companyBuy = form.watch('company_buy');
  const isDeliveryDifferent = form.watch('different_delivery_address');
  const hideDifferentDeliveryAddress = form.watch('hide_different_delivery_address');

  return (
    <div className={cn('flex w-full flex-col gap-6', 'ea-cart-order-form')}>
      <div
        className={cn(
          'flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6',
          'ea-cart-order-form__form-wrapper',
        )}
      >
        <div className="grid w-full grid-cols-12 gap-3 sm:gap-4">
          <Heading title={t('text3560')} className={{ container: 'col-span-12' }} tag="h4" />
          {!me && <SignInFormCart />}
          <Field
            required
            control={form?.control as any}
            name="email"
            type="email"
            placeholder={'E-mail'}
            before={<MdOutlineEmail size={20} className="text-[#9CA3AF]" />}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6 ' }}
          />
          <Field
            required
            control={form?.control as any}
            name="phone"
            placeholder={t('text1258')}
            type="tel"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6 ' }}
          />
          <Field
            required
            control={form?.control as any}
            name="name"
            placeholder={t('text33')}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />
          <Field
            required
            control={form?.control as any}
            name="surname"
            placeholder={t('text34')}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />
        </div>
        <Separator className="col-span-12 my-2 w-full" />
        <div className="grid w-full grid-cols-12 gap-3 rounded-[4px] sm:gap-4">
          <div className="col-span-12 flex w-full flex-col items-start justify-start gap-2.5">
            <Heading title={t('text45')} tag="h4" />
            <Paragraph color="muted-foreground" size="lg">
              {t('text3735')}
            </Paragraph>
          </div>
          <FormField
            control={form.control}
            name="company_buy"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'ea-register-form__company-type-field',
                  'col-span-12 flex w-full flex-row items-center space-x-3 space-y-0',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={!!field.value}
                    onCheckedChange={field.onChange}
                    className="h-6 w-6"
                  />
                </FormControl>
                <FormLabel className="text-sm font-medium tracking-tight sm:text-base">
                  {t('text3736')}
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
          {companyBuy ? <CompanyBuyFields /> : <CompanyFalseFields />}

          <Field
            required
            control={form?.control as any}
            name="country.id"
            type="select"
            placeholder={t('text41')}
            className={{ wrapper: 'col-span-12 w-full' }}
            options={countries?.data?.map(c => ({ value: c.id, label: t(c.name) })) ?? []}
          />
          <Field
            control={form?.control as any}
            name="note"
            placeholder={`${t('text57')}...`}
            type="textarea"
            className={{ wrapper: 'col-span-12 w-full' }}
          />
        </div>
        {/* A pickup point (CUSTOM_PLACE) fills the delivery address itself and sets
            `hide_different_delivery_address` — as in core, the editable delivery block must stay
            hidden then, or the customer can overwrite the pickup point's address. */}
        {!hideDifferentDeliveryAddress && (
          <>
            <Separator className="col-span-12 my-2 w-full" />
            <FormField
              control={form?.control as any}
              name="different_delivery_address"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'col-span-12 flex w-full flex-row items-center space-x-3 space-y-0',
                    'ea-cart-order-form__delivery-toggle-field',
                  )}
                >
                  <FormControl>
                    <Checkbox
                      ref={field.ref}
                      name={field.name}
                      disabled={field.disabled}
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                      className="h-6 w-6"
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-medium tracking-tight sm:text-base">
                    {t('text3737')}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isDeliveryDifferent && (
              <div className="grid w-full grid-cols-12 gap-3 rounded-[4px] sm:gap-4">
                <Heading title={t('text2139')} className={{ container: 'col-span-12' }} tag="h4" />
                <DeliveryFields />
              </div>
            )}
          </>
        )}

        {/* <Field
        control={form.control}
        name="conditions"
        type="checkbox"
        label={t('text336')}
        required
      /> */}
        {/* Heureka consent lives only on the summary step (opt-in, text2414). This step used to
            bind the same field to the opposite wording ("Nesouhlasím…", text2015). */}
        <FormField
          control={form?.control as any}
          name="gdpr"
          render={({ field }) => (
            <FormItem
              className={cn(
                'flex flex-row items-start space-x-3 space-y-0',
                'ea-cart-order-form__gdpr-field',
              )}
              required
            >
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="h-6 w-6"
                />
              </FormControl>
              <div className="flex flex-col gap-1">
                <FormLabel className="pl-1">
                  <p className="text-sm text-black lg:text-base">
                    {t('text264')}{' '}
                    <Link
                      href={getTosUrl(locale)}
                      className="text-sm font-semibold text-primary underline lg:text-base"
                      prefetch={false}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('text265')}
                    </Link>
                    {'. '}
                    <Link
                      href={getGdprUrl()}
                      className="text-sm font-semibold text-primary underline lg:text-base"
                      prefetch={false}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('text364')}
                    </Link>
                  </p>
                </FormLabel>
                <FormMessage className="pl-1" />
              </div>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

function CompanyFalseFields() {
  const { t } = useTranslations();
  const form = useFormContext();

  return (
    <>
      <Field
        required
        control={form.control}
        name="address"
        label={t('text157')}
        placeholder={t('text157')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        required
        control={form.control}
        name="street_number"
        label={t('text438')}
        placeholder={t('text438')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        required
        control={form.control}
        name="postcode"
        label={t('text40')}
        placeholder={t('text40')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        required
        control={form.control}
        name="city"
        label={t('text39')}
        placeholder={t('text39')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
    </>
  );
}

function CompanyBuyFields() {
  const { t } = useTranslations();
  const form = useFormContext<CreateOrderFormValues>();
  const vatContext = useCartVatContext();
  const isInvoiceCountrySK = String(form.watch('country.id')).toUpperCase() === 'SK';

  return (
    <>
      <div
        className="ea-cart-order-form__vat-fields contents"
        onBlurCapture={event => {
          const fieldName = (event.target as HTMLInputElement).name;
          if (fieldName === 'ic_dph' || (fieldName === 'dic' && !isInvoiceCountrySK)) {
            void vatContext.checkVatId();
          }
        }}
      >
        <CartVatCompanyDialog
          result={vatContext.pendingCompanyData}
          isLoading={vatContext.isResolving}
          onConfirm={() => void vatContext.confirmCompanyData()}
          onReject={() => void vatContext.rejectCompanyData()}
        />
        <Field
          required
          control={form?.control as any}
          name="company"
          label={t('text1188')}
          placeholder={t('text1188')}
          className={{ wrapper: 'col-span-12 w-full' }}
        />
        <Field
          required
          control={form?.control as any}
          name="ico"
          label={t('text42')}
          placeholder={t('text42')}
          className={{ wrapper: 'col-span-12 w-full lg:col-span-6' }}
        />
        <Field
          control={form?.control as any}
          name="dic"
          label={t('text43')}
          placeholder={t('text43')}
          className={{ wrapper: 'col-span-12 w-full lg:col-span-6' }}
        />
        {isInvoiceCountrySK && (
          <Field
            control={form?.control as any}
            name="ic_dph"
            label={t('text44')}
            placeholder={t('text44')}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
        )}
        {(vatContext.isProcessing ||
          vatContext.message ||
          vatContext.isValid ||
          vatContext.isInvalid) && (
          <div
            className={cn(
              'col-span-12 flex items-center gap-2 text-sm',
              'ea-cart-order-form__vat-context-message',
            )}
          >
            {vatContext.isProcessing ? (
              <Spinner className="h-4 w-4 text-primary" />
            ) : vatContext.isInvalid ? (
              <CrossCircledIcon className="h-5 w-5 text-destructive" />
            ) : vatContext.isValid ? (
              <CheckCircledIcon className="h-5 w-5 text-success-foreground" />
            ) : undefined}
            {vatContext.message && (
              <p
                role="status"
                className={cn(
                  vatContext.messageVariant === 'success'
                    ? 'text-success-foreground'
                    : vatContext.messageVariant === 'error'
                      ? 'text-destructive'
                      : 'text-muted-foreground',
                )}
              >
                {vatContext.message}
              </p>
            )}
          </div>
        )}
        <Field
          required
          control={form?.control as any}
          name="address"
          label={t('text157')}
          placeholder={t('text157')}
          className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
        />
        <Field
          required
          control={form?.control as any}
          name="street_number"
          label={t('text438')}
          placeholder={t('text438')}
          className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
        />
        <Field
          required
          control={form?.control as any}
          name="postcode"
          label={t('text40')}
          placeholder={t('text40')}
          className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
        />
        <Field
          required
          control={form?.control as any}
          name="city"
          label={t('text39')}
          placeholder={t('text39')}
          className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
        />
      </div>
    </>
  );
}

function DeliveryFields() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const form = useFormContext();

  const { data: countries } = useSuspenseQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <Field
        required
        control={form.control}
        name="address2"
        label={t('text157')}
        placeholder={t('text157')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        required
        control={form.control}
        name="street_number2"
        label={t('text485')}
        placeholder={t('text485')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        required
        control={form.control}
        name="postcode2"
        label={t('text40')}
        placeholder={t('text40')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        required
        control={form.control}
        name="city2"
        label={t('text39')}
        placeholder={t('text39')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        control={form.control}
        name="country2.id"
        label={t('text519')}
        placeholder={t('text519')}
        type="select"
        options={countries?.data?.map(c => ({ value: c.id, label: t(c.name) })) ?? []}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
    </>
  );
}
